/**
 * SlaCard - Card "SLA & Tempos" da sidebar T1.
 *
 * Cálculos portados 1:1 do `TicketDetailSidebar` legado (SLA countdown,
 * tempo de resposta, datas). Acrescenta uma barra de progresso real
 * (criadoEm → slaDueAt/slaBreachTime) — dado que já existe, só não tinha
 * visualização; NÃO fabrica números que o backend não expõe (ex.: não existe
 * um alvo de "1ª resposta" separado, então esse continua como duração simples).
 */
import React from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { SidebarCard } from './SidebarCard';
import { SidebarField } from '../SidebarField';
import type { PopulatedTicket } from '../../types';
import { formatDuration } from '../../utils/formatDuration';
import { Icon } from '@/shared/icons';

export interface SlaCardProps {
  ticket: PopulatedTicket;
}

export const SlaCard: React.FC<SlaCardProps> = ({ ticket }) => {
  const getSlaInfo = () => {
    const dueAt = ticket.slaDueAt || ticket.slaBreachTime;
    if (!dueAt) return null;
    const now = new Date();
    const due = new Date(dueAt);
    const diffMs = due.getTime() - now.getTime();
    const diffMinutes = diffMs / 60000;

    if (diffMs <= 0) return { label: 'SLA violado', color: '#EF4444', percent: 100 };
    const hoursRemaining = diffMinutes / 60;
    const color = hoursRemaining >= 4 ? '#10B981' : hoursRemaining >= 1 ? '#F59E0B' : '#EF4444';

    let percent: number | null = null;
    if (ticket.createdAt) {
      const created = new Date(ticket.createdAt).getTime();
      const total = due.getTime() - created;
      if (total > 0) {
        percent = Math.min(100, Math.max(0, ((now.getTime() - created) / total) * 100));
      }
    }

    return { label: `${formatDuration(diffMinutes)} restantes`, color, percent };
  };

  const slaInfo = getSlaInfo();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'dd/MM HH:mm', { locale: ptBR });
    } catch {
      return '—';
    }
  };

  const formatRelative = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR });
    } catch {
      return '—';
    }
  };

  return (
    <SidebarCard icon="clock" title="SLA & Tempos" collapsible defaultCollapsed data-testid="ticket-detail-sla-card">
      {slaInfo ? (
        <div
          className="py-1.5"
          style={{ borderBottom: '1px solid var(--theme-card-border, #f1f5f9)' }}
          data-testid="ticket-detail-sla-field"
        >
          <div className="flex items-center gap-1">
            {slaInfo.label === 'SLA violado' ? (
              <Icon name="alert-triangle" className="w-4 h-4" style={{ color: slaInfo.color }} />
            ) : (
              <Icon name="clock" className="w-4 h-4" style={{ color: slaInfo.color }} />
            )}
            <span className="text-[15px] font-extrabold" style={{ color: slaInfo.color }}>
              {slaInfo.label}
            </span>
          </div>
          {slaInfo.percent != null && (
            <div
              className="w-full h-1.5 rounded-full overflow-hidden mt-1.5"
              style={{ backgroundColor: 'var(--theme-button-ghost, #f1f5f9)' }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${slaInfo.percent}%`, backgroundColor: slaInfo.color }}
              />
            </div>
          )}
          <div className="mt-1 text-[11px]" style={{ color: 'var(--theme-muted-text, #94a3b8)' }}>
            Criado {formatDate(ticket.createdAt)} · limite {formatDate(ticket.slaDueAt || ticket.slaBreachTime)}
          </div>
        </div>
      ) : (
        <SidebarField label="SLA" data-testid="ticket-detail-sla-field">
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
        </SidebarField>
      )}

      <SidebarField label="Tempo resposta" data-testid="ticket-detail-response-time-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>
          {ticket.responseTime != null ? formatDuration(ticket.responseTime) : '—'}
        </span>
      </SidebarField>

      <SidebarField label="Tempo em andamento" data-testid="ticket-detail-time-in-progress-field">
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
      </SidebarField>

      <SidebarField label="Tempo aguardando" data-testid="ticket-detail-time-waiting-field">
        <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>—</span>
      </SidebarField>

      <div
        className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: 'var(--theme-muted-text, #94a3b8)' }}
      >
        Informações
      </div>

      <SidebarField label="Criado em" data-testid="ticket-detail-created-at-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{formatDate(ticket.createdAt)}</span>
      </SidebarField>

      <SidebarField label="1ª resposta" data-testid="ticket-detail-first-response-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{formatDate(ticket.firstResponseAt)}</span>
      </SidebarField>

      <SidebarField label="Atualizado" data-testid="ticket-detail-updated-at-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{formatRelative(ticket.updatedAt)}</span>
      </SidebarField>

      <SidebarField label="Resolvido em" data-testid="ticket-detail-resolved-at-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{formatDate(ticket.resolvedAt)}</span>
      </SidebarField>

      <SidebarField label="Fechado em" data-testid="ticket-detail-closed-at-field">
        <span className="text-xs" style={{ color: 'var(--theme-card-text)' }}>{formatDate(ticket.closedAt)}</span>
      </SidebarField>

      {/* Satisfação do cliente — só aparecia na antiga aba "Detalhes"; preservada aqui */}
      {ticket.customerSatisfaction != null && (
        <SidebarField label="Satisfação" data-testid="ticket-detail-customer-satisfaction-field">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon
                key={star}
                name="star"
                className="w-3 h-3"
                style={{
                  color: star <= (ticket.customerSatisfaction || 0) ? '#F59E0B' : 'var(--theme-card-border)',
                  fill: star <= (ticket.customerSatisfaction || 0) ? '#F59E0B' : 'none',
                }}
              />
            ))}
          </div>
        </SidebarField>
      )}
    </SidebarCard>
  );
};

SlaCard.displayName = 'SlaCard';
